import { useCallback, useEffect, useState } from 'react';
import { ApiClient } from 'utils/api/ApiClient';
import { useAuthContext } from 'utils/auth/auth-context';
import { createLogger } from 'utils/logging/logging-utils';
import { useUiContext } from 'utils/react/ui-context';
import { ArgsOfSkip1, PromiseOf } from 'utils/types/misc';
import { RemoteData } from 'utils/types/remoteData';

/**
 * Helper hook for using api data in a component
 * Note that the data is stored on the component level, so automatic reuse is not available
 *
 * @returns [apiData, resetApiData, silentRefetchApiData]
 */
export function useApiData<M extends keyof ApiClient>(method: M, params: ArgsOfSkip1<ApiClient[M]>) {
  const { apiClient, config } = useUiContext();
  const { authState } = useAuthContext();
  const [apiData, setApiData] = useState<RemoteData<PromiseOf<ReturnType<ApiClient[M]>>>>({
    status: 'REMOTE_DATA_NOT_ASKED',
  });
  const log = createLogger(config, 'ApiData');

  const accessToken = authState.type === 'LoggedIn' && authState.accessToken;
  const fetchData = useCallback((): Promise<void> => {
    if (accessToken) {
      log(`Fetching with method "${method}" with params`, params);
      // TS-fu is not sufficient and we need to resort to "as any" to handle the different api client methods
      // We'll assume that because everything outside this method is well-typed that we're off the hook.
      // There's also runtime validation to make sure no-one's invoking any illegal methods on the API client.
      if (apiClient.hasOwnProperty(method)) {
        return (apiClient as any)[method]
          .apply(null, [accessToken, ...params])
          .then((data: PromiseOf<ReturnType<ApiClient[M]>>) => setApiData({ status: 'REMOTE_DATA_SUCCESS', data }))
          .catch((err: any) =>
            setApiData({
              status: 'REMOTE_DATA_FAILURE',
              error: `${err.message}`,
              errorCode:
                err.response && typeof err.response.status === 'number' ? (err.response.status as number) : undefined,
            }),
          );
      } else {
        setApiData({ status: 'REMOTE_DATA_FAILURE', error: `Unknown method "${method}" specified for ApiClient` });
      }
    } else {
      log('Failure: tried to fetch without access token.');
      setApiData({ status: 'REMOTE_DATA_FAILURE', error: 'No access token found.' });
    }
    return Promise.resolve();
  }, [log, method, params, apiClient, accessToken]);

  useEffect(() => {
    if (apiData.status === 'REMOTE_DATA_NOT_ASKED' && accessToken) {
      setApiData({ status: 'REMOTE_DATA_LOADING' });
      fetchData();
    }
  }, [apiData, fetchData, accessToken]);

  const resetApiData = () => setApiData({ status: 'REMOTE_DATA_NOT_ASKED' });

  return [apiData, resetApiData, fetchData] as const;
}

// Helper hook for using post/put api calls in a component without passing the auth access token around
export function useApiClient<M extends keyof ApiClient>(
  method: M,
): (...args: ArgsOfSkip1<ApiClient[M]>) => ReturnType<ApiClient[M]> {
  const { apiClient } = useUiContext();
  const { authState } = useAuthContext();

  const accessToken = authState.type === 'LoggedIn' && authState.accessToken;
  if (!accessToken) {
    return () => Promise.reject(new Error('No access token found.')) as any;
  }

  return (apiClient[method] as any).bind(null, accessToken);
}

export function useHandleFailedApiRequest(setErrorMessage: (s: string) => void, setLoading: (v: boolean) => void) {
  return (errorMessage: string) => (err: any) => {
    const statusCode = err && err.response && err.response.status;
    const message = `${errorMessage}${
      statusCode !== undefined ? ` (${statusCode}).` : ''
    } Please try again. If it fails, refresh the page.`;
    setErrorMessage(message);
    setLoading(false);
  };
}
